import { apiClient, createApiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../../../api/apiClient';
import { buildTrendFilters, TrendFilters, ApiEnvelope, ApiErrorEnvelope } from './completedTrendApi';
import type { AppliedFiltersSnapshot } from '../OnTimeDispatchDashboard';

// Not time-based — this is a diverging-bar-by-entity chart, keyed on hierarchy instead of timeframe.
export type VarianceHierarchyGrain = 'region' | 'market' | 'location';
export type VarianceHierarchy = 'REGION_UNIT' | 'MARKET_POD' | 'LOCATION';

export type VarianceFilters = TrendFilters;

export interface VarianceChartConfig {
  hierarchy: VarianceHierarchy;
}

export interface VarianceRequest {
  filters: VarianceFilters;
  chartConfiguration: VarianceChartConfig;
}

export interface VarianceEntity {
  entityId: string;
  entityName: string;
  value: number;
  variancePp: number;
}

export interface VarianceResponseData {
  metric: string;
  target: number | null;
  subtitle: string | null;
  hierarchy: VarianceHierarchy;
  data: VarianceEntity[];
  maxAbsoluteVariance: number | null;
  noData: boolean;
  message: string | null;
}

export class VarianceApiError extends Error {
  status: number;
  path?: string;
  fieldErrors?: ApiErrorEnvelope['fieldErrors'];

  constructor(message: string, status: number, path?: string, fieldErrors?: ApiErrorEnvelope['fieldErrors']) {
    super(message);
    this.name = 'VarianceApiError';
    this.status = status;
    this.path = path;
    this.fieldErrors = fieldErrors;
  }
}

const HIERARCHY_MAP: Record<VarianceHierarchyGrain, VarianceHierarchy> = {
  region: 'REGION_UNIT',
  market: 'MARKET_POD',
  location: 'LOCATION',
};

// Reuses the trend endpoints' filters shape unchanged; only chartConfiguration differs (hierarchy, not timeframe).
export function buildVarianceRequest(
  appliedFilters: AppliedFiltersSnapshot,
  hierarchyGrain: VarianceHierarchyGrain
): VarianceRequest {
  return {
    filters: buildTrendFilters(appliedFilters),
    chartConfiguration: {
      hierarchy: HIERARCHY_MAP[hierarchyGrain],
    },
  };
}

const api = createApiClient();

interface GetVarianceOptions {
  timeoutMs?: number;
  signal?: AbortSignal;
}

function isApiErrorEnvelope(value: unknown): value is ApiErrorEnvelope {
  return !!value && typeof value === 'object' && 'message' in value && 'status' in value;
}

// Shared POST + error handling for every variance endpoint; only the URL and fallback message differ per metric.
async function postVariance(
  endpoint: string,
  payload: VarianceRequest,
  metricLabel: string,
  options?: GetVarianceOptions
): Promise<ApiEnvelope<VarianceResponseData>> {
  try {
    const response = await api.post<ApiEnvelope<VarianceResponseData>>(
      endpoint,
      payload,
      {
        params: {
          clientTimestamp: getLocalTimestamp(),
          clientTimezone: getTimezoneOffsetString(),
        },
        timeout: options?.timeoutMs,
        signal: options?.signal,
      }
    );
    return response.data;
  } catch (error: any) {
    const responseData = error?.response?.data;
    if (isApiErrorEnvelope(responseData)) {
      throw new VarianceApiError(responseData.message, responseData.status, responseData.path, responseData.fieldErrors);
    }
    const status = error?.response?.status ?? 0;
    const message = error?.message || `Failed to load ${metricLabel} data`;
    throw new VarianceApiError(message, status);
  }
}

const COMPLETED_VARIANCE_ENDPOINT = '/ontime-dispatch/completed-variance';
const ONTIME_VARIANCE_ENDPOINT = '/ontime-dispatch/ontime-variance';
const DRIVER_ARRIVED_VARIANCE_ENDPOINT = '/ontime-dispatch/driver-arrived-variance';

export async function getCompletedVariance(
  payload: VarianceRequest,
  options?: GetVarianceOptions
): Promise<ApiEnvelope<VarianceResponseData>> {
  return postVariance(COMPLETED_VARIANCE_ENDPOINT, payload, 'Completed Variance', options);
}

export async function getOnTimeVariance(
  payload: VarianceRequest,
  options?: GetVarianceOptions
): Promise<ApiEnvelope<VarianceResponseData>> {
  return postVariance(ONTIME_VARIANCE_ENDPOINT, payload, 'On-Time Variance', options);
}

export async function getDriverArrivalVariance(
  payload: VarianceRequest,
  options?: GetVarianceOptions
): Promise<ApiEnvelope<VarianceResponseData>> {
  return postVariance(DRIVER_ARRIVED_VARIANCE_ENDPOINT, payload, 'Driver Arrival Variance', options);
}
